import { z } from 'zod';
import { findAirport, fold } from './aviation';

/** The quick search (⌘K): what the API is asked for. */
export const searchQuery = z.object({
  q: z.string().trim().min(1).max(100),
});

export type QuickIntent = 'sale' | 'proposal' | 'collection';

/** What the quick search understood in what was typed, besides plain words. */
export interface QuickQuery {
  /** The text to look up in records, with any intent words taken out. */
  term: string;
  /** "… için satış", "teklif …", "tahsilat …": what to do with the record found. */
  intent: QuickIntent | null;
  /** A day ("yarın", "3 aralık", "12.11", "cuma"), as YYYY-MM-DD. */
  day: string | null;
  /** A month on its own ("aralık", "ocak 2027"), as YYYY-MM. */
  month: string | null;
  /** Digits of a phone number, without the country code or leading zero. */
  phone: string | null;
  email: string | null;
  /** Airport codes: one ("AYT") or a route ("IST-FRA", "ist fra"). */
  airports: string[];
}

const MONTHS = [
  'ocak',
  'subat',
  'mart',
  'nisan',
  'mayis',
  'haziran',
  'temmuz',
  'agustos',
  'eylul',
  'ekim',
  'kasim',
  'aralik',
];
const WEEKDAYS = ['pazar', 'pazartesi', 'sali', 'carsamba', 'persembe', 'cuma', 'cumartesi'];

/** Longest phrases first, so "için yeni satış" is taken whole. */
const INTENTS: { intent: QuickIntent; phrases: string[] }[] = [
  {
    intent: 'sale',
    phrases: [
      'icin yeni satis',
      'icin satis ac',
      'icin satis',
      'yeni satis',
      'satis ac',
      'satis olustur',
      'satis',
    ],
  },
  {
    intent: 'proposal',
    phrases: [
      'icin teklif hazirla',
      'icin yeni teklif',
      'icin teklif',
      'teklif hazirla',
      'yeni teklif',
      'teklif',
    ],
  },
  {
    intent: 'collection',
    phrases: ['tahsilat ekle', 'tahsilat gir', 'odeme al', 'tahsilat'],
  },
];

const pad = (n: number) => String(n).padStart(2, '0');
const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function validDay(year: number, month: number, day: number): string | null {
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
    ? isoDay(d)
    : null;
}

/** A day written without a year means the next one to come (today counts). */
function upcoming(today: Date, month: number, day: number): string | null {
  const thisYear = validDay(today.getFullYear(), month, day);
  if (thisYear && thisYear >= isoDay(today)) return thisYear;
  return validDay(today.getFullYear() + 1, month, day);
}

const monthIndex = (word: string) =>
  word.length >= 3 ? MONTHS.findIndex((m) => m.startsWith(word)) : -1;

function parseDay(text: string, today: Date): { day: string | null; month: string | null } {
  const t = fold(text).replace(/[,']/g, ' ').replace(/\s+/g, ' ').trim();
  const relative: Record<string, number> = { bugun: 0, yarin: 1, dun: -1, 'obur gun': 2 };
  if (t in relative) {
    const d = new Date(today);
    d.setDate(d.getDate() + relative[t]!);
    return { day: isoDay(d), month: null };
  }
  const weekday = WEEKDAYS.indexOf(t);
  if (weekday >= 0) {
    const d = new Date(today);
    d.setDate(d.getDate() + ((weekday - d.getDay() + 7) % 7));
    return { day: isoDay(d), month: null };
  }
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) return { day: validDay(+m[1]!, +m[2]!, +m[3]!), month: null };
  m = /^(\d{1,2})[./](\d{1,2})(?:[./](\d{2}|\d{4}))?$/.exec(t);
  if (m) {
    const year = m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : null;
    return {
      day: year ? validDay(year, +m[2]!, +m[1]!) : upcoming(today, +m[2]!, +m[1]!),
      month: null,
    };
  }
  m = /^(\d{1,2}) ([a-z]+)(?: (\d{4}))?$/.exec(t);
  if (m) {
    const month = monthIndex(m[2]!);
    if (month >= 0) {
      return {
        day: m[3] ? validDay(+m[3], month + 1, +m[1]!) : upcoming(today, month + 1, +m[1]!),
        month: null,
      };
    }
  }
  m = /^([a-z]+)(?: (\d{4}))?$/.exec(t);
  if (m && m[1]!.length >= 4) {
    const month = MONTHS.indexOf(m[1]!);
    if (month >= 0) {
      let year = m[2] ? +m[2] : today.getFullYear();
      if (!m[2] && month < today.getMonth()) year += 1;
      return { day: null, month: `${year}-${pad(month + 1)}` };
    }
  }
  return { day: null, month: null };
}

/**
 * Digits of something that looks like a phone number, as the search keys store
 * them: "0532 555 10 01" and "+90 532 555 10 01" both give 5325551001.
 */
export function phoneDigits(text: string): string | null {
  if (!/^\+?[\d\s()./-]+$/.test(text.trim())) return null;
  let digits = text.replace(/\D/g, '');
  if (digits.length < 7) return null;
  if (digits.startsWith('90') && digits.length === 12) digits = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  return digits;
}

/** Airport codes in "AYT", "IST-FRA", "ist fra" or "IST → FRA"; none if any is unknown. */
export function airportCodes(text: string): string[] {
  const m = /^([a-z]{3})(?:\s*(?:-|>|→|\s)\s*([a-z]{3}))?$/i.exec(text.trim());
  if (!m) return [];
  const codes = [m[1]!, m[2]].filter((c): c is string => !!c).map((c) => c.toUpperCase());
  return codes.every((c) => findAirport(c)) ? codes : [];
}

/**
 * Reads what was typed into the quick search: an intent ("ahmet için satış"), a
 * day, a phone number, an e-mail address or airport codes, and the words left to
 * search records by. Pure, so the console and its tests agree.
 */
export function parseQuickQuery(text: string, today = new Date()): QuickQuery {
  const raw = text.trim().replace(/\s+/g, ' ');
  let term = raw;
  let intent: QuickIntent | null = null;

  const folded = fold(raw);
  search: for (const { intent: kind, phrases } of INTENTS) {
    for (const phrase of phrases) {
      for (const at of ['end', 'start'] as const) {
        const hit = at === 'end' ? folded.endsWith(` ${phrase}`) : folded.startsWith(`${phrase} `);
        if (!hit) continue;
        // Folding keeps every letter's place, so the same span is cut from the original.
        const rest =
          at === 'end'
            ? raw.slice(0, raw.length - phrase.length - 1)
            : raw.slice(phrase.length + 1);
        if (!rest.trim()) continue;
        intent = kind;
        // "Ahmet'e satış", "Ayşe'nin teklifi": drop the suffix after an apostrophe.
        term = rest.trim().replace(/['’][a-zçğıöşü]{1,4}$/iu, '');
        break search;
      }
    }
  }

  const { day, month } = parseDay(raw, today);
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(term) ? term.toLowerCase() : null;
  return {
    term,
    intent,
    day,
    month,
    phone: email ? null : phoneDigits(term),
    email,
    airports: airportCodes(term),
  };
}
